/**
 * The Arabic dictionary. Typed against `en`, so a key added there and not
 * here is a compile error.
 *
 * This is the *chrome* only. Extracted content is never translated -- an
 * English-reading user looking at an Arabic filing sees English chrome
 * around an Arabic table that is still right-to-left, still in Arabic-indic
 * digits, and still exactly as printed (§11).
 *
 * Two rules this file follows that a word-for-word translation would not:
 *
 * **The counted noun is spelled six ways, not two.** `_two` is the dual,
 * `_few` covers 3–10, `_many` covers 11–99, and the bare key is the 100+
 * form. So `43 مستنداً` and `4 مستندات` and `100 مستند` are all correct and
 * none is a variant spelling of another. See `tn` in ./index.tsx.
 *
 * **English idioms are re-said, not carried across.** "what this app exists
 * to surface" has no Arabic shape with `يوجد`; it becomes `أُنشئ هذا
 * التطبيق لكشفه`. Verb-first word order is preferred where English leads
 * with the subject, and the counterfactual takes `لو ... لـ` rather than
 * `كان سي‍ـ`.
 */
import type { Dict } from './en.ts'

export const ar: Dict = {
  // --- chrome ---------------------------------------------------------
  'app.name': 'المستكشف',
  'app.tagline': 'ما استُخرج من الإفصاحات، بجانب الورق الذي جاء منه',
  'nav.explore': 'التصفية',
  'nav.results': 'الشركات',
  'nav.about': 'المجموعة',
  'nav.back': 'رجوع',
  'action.refresh': 'تحديث',
  'action.retry': 'إعادة المحاولة',
  'action.close': 'إغلاق',
  'action.clear': 'مسح',
  'action.selectAll': 'تحديد الكل',
  'action.selectNone': 'إلغاء التحديد',
  'action.expand': 'توسيع',
  'action.collapse': 'طي',
  'action.copy': 'نسخ',
  'action.copied': 'تم النسخ',
  'action.showMore': 'عرض المزيد',
  'action.showLess': 'عرض أقل',
  'theme.light': 'فاتح',
  'theme.dark': 'داكن',
  'theme.system': 'النظام',
  'theme.label': 'المظهر',
  'lang.label': 'اللغة',
  'lang.en': 'English',
  'lang.ar': 'العربية',
  'display.label': 'العرض',
  'display.title': 'إعدادات العرض',
  'density.label': 'حجم النص وعناصر التحكم',
  'density.auto': 'تلقائي',
  'density.autoHint': 'أكبر على شاشات اللمس، وأكثف مع الفأرة.',
  'density.comfortable': 'مريح',
  'density.compact': 'مضغوط',

  // --- generic --------------------------------------------------------
  'common.loading': 'جارٍ التحميل…',
  'common.error': 'حدث خطأ',
  'common.errorWithId': 'فشل الطلب {id}.',
  'common.notRecorded': 'غير مسجَّل',
  'common.none': 'لا شيء',
  'common.of': 'من',
  'common.and': 'و',

  'common.documents': '{n} مستند',
  'common.documents_one': 'مستند واحد',
  'common.documents_two': 'مستندان',
  'common.documents_few': '{n} مستندات',
  'common.documents_many': '{n} مستنداً',

  'common.companies': '{n} شركة',
  'common.companies_one': 'شركة واحدة',
  'common.companies_two': 'شركتان',
  'common.companies_few': '{n} شركات',
  'common.companies_many': '{n} شركةً',

  'common.pages': '{n} صفحة',
  'common.pages_one': 'صفحة واحدة',
  'common.pages_two': 'صفحتان',
  'common.pages_few': '{n} صفحات',
  'common.pages_many': '{n} صفحةً',

  'common.facts': '{n} قيمة',
  'common.facts_one': 'قيمة واحدة',
  'common.facts_two': 'قيمتان',
  'common.facts_few': '{n} قيم',
  'common.facts_many': '{n} قيمةً',

  'common.checks': '{n} فحص',
  'common.checks_one': 'فحص واحد',
  'common.checks_two': 'فحصان',
  'common.checks_few': '{n} فحوص',
  'common.checks_many': '{n} فحصاً',

  'common.rows': '{n} صف',
  'common.rows_one': 'صف واحد',
  'common.rows_two': 'صفّان',
  'common.rows_few': '{n} صفوف',
  'common.rows_many': '{n} صفاً',

  // --- filter screen --------------------------------------------------
  'filter.title': 'المجموعة المُحلَّلة',
  'filter.corpusLine': '{documents} · {companies} · {from}–{to}',
  'filter.sector': 'القطاع',
  'filter.industry': 'الصناعة',
  'filter.companies': 'الشركات',
  'filter.period': 'الفترة',
  'filter.docType': 'نوع المستند',
  'filter.locale': 'اللغة',
  'filter.year': 'السنة',
  'filter.quality': 'الجودة',
  'filter.quality.any': 'الكل',
  'filter.quality.high': '‏90٪ فأكثر',
  'filter.quality.medium': '‏75–90٪',
  'filter.quality.low': 'أقل من 75٪',
  'filter.quality.note':
    'في كل مستند من هذه المجموعة فحصٌ فاشل واحد على الأقل، فمرشِّح «السليم فقط» لن يُرجع شيئاً. وهذه النطاقات تُدرّج الموجود فعلاً.',
  'filter.selected': '{n} من {total} محدَّدة',
  'filter.allIncluded': 'الكل مشمول ({total})',
  'filter.onlyOption': 'الخيار الوحيد',
  'filter.explore': 'استكشاف',
  'filter.exploreAll': 'استكشاف الكل — {documents}',
  'filter.exploreCount': '{documents} في {companies}',
  'filter.empty': 'لا يوجد ما يطابق هذا التحديد',
  'filter.emptyHint': 'وسِّع أحد عوامل التصفية — {hint}',
  'filter.pruned': 'أُزيلت {n} — لم تعد ضمن {level}',
  'filter.pruned_one': 'أُزيلت واحدة — لم تعد ضمن {level}',
  'filter.pruned_two': 'أُزيلت اثنتان — لم تعدا ضمن {level}',
  'filter.passRate': 'نسبة النجاح',
  'filter.docsShort': '{n} مستند',
  'filter.reset': 'إعادة ضبط المرشِّحات',

  // --- results --------------------------------------------------------
  'results.title': 'الشركات',
  'results.count': '{companies} · {documents}',
  'results.editFilter': 'تعديل التصفية',
  'results.sort': 'الترتيب',
  'results.sort.symbol': 'الرمز',
  'results.sort.name': 'الاسم',
  'results.sort.documents': 'عدد المستندات',
  'results.sort.quality': 'نسبة النجاح',
  'results.coverage': 'التغطية',
  'results.coverageLegend': 'المملوء = مُحلَّل',
  'results.openReview': '{n} مفتوحة',
  'results.passShort': 'نجاح {pct} من الفحوص',
  'results.empty': 'لا توجد شركة تطابق هذه التصفية',
  'results.emptyHint': 'عُد إلى التصفية ووسِّع نطاقها.',

  // --- company --------------------------------------------------------
  'company.parsedDocuments': '{n} مستند مُحلَّل',
  'company.parsedDocuments_one': 'مستند واحد مُحلَّل',
  'company.parsedDocuments_two': 'مستندان مُحلَّلان',
  'company.parsedDocuments_few': '{n} مستندات مُحلَّلة',
  'company.parsedDocuments_many': '{n} مستنداً مُحلَّلاً',
  'company.tab.overview': 'نظرة عامة',
  'company.tab.financials': 'القوائم المالية',
  'company.tab.notes': 'الإيضاحات',
  'notes.numberFrom': 'رقم الإيضاح مأخوذ من تصنيف الصفحة. وحين يختلف عن الرقم المطبوع بجانبه، فالاختلاف نفسه جزءٌ من البيانات، لا خللٌ في العرض.',
  'company.tab.auditor': 'المراجع',
  'company.tab.documents': 'المستندات',
  'company.tab.quality': 'الجودة',
  'company.tab.governance': 'الحوكمة',
  'company.tab.remuneration': 'المكافآت',
  'company.tab.subsidiaries': 'الشركات التابعة',

  // --- announcements, and the cross-check against them ----------------
  'company.tab.announcements': 'إعلانات الشركة',
  'company.tab.crosscheck': 'المطابقة',

  'ann.none': 'لا توجد إعلانات مُلتقطة',
  'ann.noneDetail': 'شغّل capture/company_announcements.py لهذه الشركة.',
  'ann.total': 'الإعلانات',
  'ann.financial': 'إعلانات النتائج',
  'ann.financialHint': 'التي تحمل أرقامًا',
  'ann.figures': 'الأرقام',
  'ann.span': 'المدة',
  'ann.units': 'معاملات الوحدات',
  'ann.unitsVary': 'أكثر من واحد — لا تفترض وحدة',
  'ann.unit': 'الوحدة',
  'ann.era': 'القالب',
  'ann.scope.financial': 'النتائج فقط',
  'ann.scope.all': 'الكل',
  'ann.filter': 'تصفية بالعنوان أو الربع أو الرقم…',
  'ann.truncated': 'تُعرض أحدث الإعلانات فقط.',
  'ann.correction': 'تصحيح',
  'ann.kind.annual': 'سنوي',
  'ann.kind.interim': 'أولي',
  'ann.openEn': 'الإعلان (إنجليزي)',
  'ann.openAr': 'الإعلان (عربي)',
  'ann.noFigures': 'هذا الإعلان لا يحمل جدول أرقام.',
  'ann.item': 'البند',
  'ann.value': 'القيمة',
  'ann.priorYear': 'العام السابق',
  'ann.change': 'التغير',
  'ann.priorQuarter': 'الربع السابق',
  'ann.changeQ': 'التغير عن الربع السابق',
  'ann.gridHint.quarter': 'الأشهر الثلاثة المنفصلة',
  'ann.gridHint.period': 'التراكمي منذ بداية العام',
  'ann.gridHint.annual': 'السنة كاملة',

  'crosscheck.none': 'لم تُنفَّذ المطابقة بعد',
  'crosscheck.noneDetail': 'شغّل parser.validate_announcements لهذه الشركة.',
  'crosscheck.accuracy': "دقة الاستخراج",
  'crosscheck.accuracyHint': "باستثناء الاختلافات التي ليست من عندنا",
  'crosscheck.ourFaults': "أخطاؤنا",
  'crosscheck.ourFaultsHint': "أخطأ الاستخراج في قراءة الصفحة",
  'crosscheck.exactWithin': "{exact} تطابق تام · {within} ضمن الدقة",
  'crosscheck.agreementHint': "خام — اتفاق المصدرين حرفيًا",
  'crosscheck.notDefects': "ليست أخطاء",
  'crosscheck.notDefectsHint': "العائد مقابل الإجمالي، وإعادة العرض",
  'crosscheck.issuerSide': "إفصاح الشركة",
  'crosscheck.issuerSideHint': "الإعلان هو الطرف المخطئ",
  'crosscheck.comparisons': 'المقارنات',
  'crosscheck.agreement': 'نسبة التطابق',
  'crosscheck.exact': 'تطابق تام',
  'crosscheck.exactHint': 'حتى الريال',
  'crosscheck.within': 'ضمن الدقة',
  'crosscheck.withinHint': 'ضمن تقريب النموذج نفسه',
  'crosscheck.mismatches': 'الاختلافات',
  'crosscheck.errWarn': '{errors} خطأ / {warnings} تنبيه',
  'crosscheck.documents': 'المستندات',
  'crosscheck.allAgree': 'جميع المقارنات متطابقة ضمن دقة الإعلان نفسه.',
  'crosscheck.byCause': 'النتائج حسب السبب',
  'crosscheck.byCauseHint': 'مُصنَّفة حسب حقيقة الاختلاف — فالمعالجة تختلف بين مجموعة وأخرى.',
  'crosscheck.group.parser': 'من عندنا',
  'crosscheck.group.issuer': 'من الشركة',
  'crosscheck.group.benign': 'ليس خطأ',
  'crosscheck.error': 'خطأ',
  'crosscheck.warning': 'تنبيه',
  'crosscheck.extracted': 'المُستخرَج',
  'crosscheck.announced': 'المُعلَن',
  'crosscheck.delta': 'الفرق',
  'crosscheck.tolerance': 'حد التسامح',
  'crosscheck.shape': 'نمط الفرق',
  'crosscheck.matchedBy': 'طريقة المطابقة',
  'crosscheck.matched.label': 'اسم الصف',
  'crosscheck.matched.concept': 'المفهوم',
  'crosscheck.printedScale': 'معامل مطبَّق {printed}',
  'crosscheck.printedUnit': 'مطبوع {printed} × وحدة {unit}',
  'crosscheck.settled': 'محسوم حسابيًا — دون الرجوع إلى الحَكَم',
  'crosscheck.ownQuarters': 'مجموع أرباعه يساوي',
  'crosscheck.judge': 'حكم الذكاء الاصطناعي',
  'crosscheck.confidence': 'درجة الثقة',
  'crosscheck.whereToLook': 'أين تبحث',
  'crosscheck.evidence': 'الأدلة الكاملة',
  'crosscheck.agreements': 'جميع الحالات المتطابقة',
  'crosscheck.agreementsHint': 'كل مقارنة ناجحة، موثّقة بالطريقة نفسها — وهي الدليل على أن الفحص ما زال يطابق.',
  'crosscheck.period': 'الفترة',
  'crosscheck.lineItem': 'البند',
  'crosscheck.grid': 'الجدول',
  'crosscheck.source': 'صفحة المصدر',
  'crosscheck.announcement': 'الإعلان',
  'crosscheck.openPage': 'افتح الصفحة {page} في العارض',
  'crosscheck.anId': 'رقم الإعلان',
  'crosscheck.factId': 'معرّف البند',
  'crosscheck.documentId': 'معرّف المستند',
  'crosscheck.page': 'الصفحة',
  'crosscheck.form': 'النموذج',
  'crosscheck.published': 'تاريخ النشر',
  'crosscheck.locale': 'اللغة',
  'crosscheck.columnRole': 'دور العمود',
  'crosscheck.rowLabel': 'اسم الصف',
  'crosscheck.columnLabel': 'عنوان العمود',
  'crosscheck.periodType': 'نوع الفترة',
  'crosscheck.yes': 'نعم',
  'crosscheck.priorYearMatch': 'يساوي رقم العام السابق',
  'crosscheck.gridName.quarter': 'الربع',
  'crosscheck.gridName.period': 'التراكمي',
  'crosscheck.gridName.annual': 'السنوي',
  'crosscheck.gridName.accumulated': 'المتراكم',
  'crosscheck.concept.revenue': 'الإيرادات',
  'crosscheck.concept.gross_profit': 'مجمل الربح',
  'crosscheck.concept.operating_profit': 'الربح التشغيلي',
  'crosscheck.concept.net_profit': 'صافي الربح',
  'crosscheck.concept.total_comprehensive_income': 'إجمالي الدخل الشامل',
  'crosscheck.concept.total_shareholders_equity': 'إجمالي حقوق المساهمين',
  'crosscheck.concept.eps': 'ربحية السهم',
  'crosscheck.verdict.extraction_error': 'خطأ استخراج',
  'crosscheck.verdict.announcement_error': 'خطأ في الإعلان',
  'crosscheck.verdict.legitimate_difference': 'اختلاف مشروع',
  'crosscheck.verdict.concept_mismatch': 'بندان مختلفان',
  'crosscheck.verdict.unclear': 'غير واضح',
  'crosscheck.cause.parse_digit': 'خطأ في قراءة رقم',
  'crosscheck.cause.parse_scale': 'معامل وحدات خاطئ',
  'crosscheck.cause.parse_sign': 'إشارة مفقودة أو زائدة',
  'crosscheck.cause.parse_column': 'قراءة العمود الخطأ',
  'crosscheck.cause.parse_other': 'فرق غير مُفسَّر',
  'crosscheck.cause.announcement_arithmetic': 'الإعلان يناقض نفسه',
  'crosscheck.cause.announcement_unit': 'خطأ وحدات من الشركة',
  'crosscheck.cause.announcement_value': 'الإعلان يخالف القوائم',
  'crosscheck.cause.scope_attributable': 'العائد للمساهمين مقابل الإجمالي',
  'crosscheck.cause.scope_restated': 'أرقام مقارنة مُعاد عرضها',
  'crosscheck.cause.concept_mismatch': 'بندان مختلفان',
  'crosscheck.cause.unresolved': 'غير محدَّد',
  'crosscheck.causeHint.parse_digit': 'قرأ الاستخراج رقمًا واحدًا خطأً — غالبًا خلط بين الأرقام العربية الهندية (٧/٨، ٠/٥، ١/٢). الإصلاح في طبقة الاستخراج.',
  'crosscheck.causeHint.parse_scale': 'القيمة المستخرجة تبعد مضاعفًا عشريًا صريحًا عن المعلنة: طُبِّق معامل التقريب في المستند خطأً أو لم يُطبَّق.',
  'crosscheck.causeHint.parse_sign': 'المقدار نفسه بإشارة معاكسة — قيمة سالبة بين قوسين قُرئت موجبة أو العكس.',
  'crosscheck.causeHint.parse_column': 'القيمة المستخرجة تساوي رقم العام السابق في الإعلان، أي أن الاستخراج قرأ عمودًا مجاورًا.',
  'crosscheck.causeHint.parse_other': 'الفرق لا يأخذ نمطًا معروفًا. افتح الصفحة واقرأ الأدلة.',
  'crosscheck.causeHint.announcement_arithmetic': 'مجموع أرباع الإعلان لا يساوي رقمه التراكمي، والاستخراج يطابق الأرباع. الخطأ في نموذج الإفصاح لا في القراءة — مُثبَت حسابيًا لا مُستنتَج.',
  'crosscheck.causeHint.announcement_unit': 'الأرقام متطابقة والمقدار مختلف: أدخلت الشركة الرقم بوحدة بينما النموذج يعلن وحدة أخرى. الاستخراج صحيح والإعلان لا.',
  'crosscheck.causeHint.announcement_value': 'الرقم المعلن مختلف فعليًا عن القوائم المراجعة — غالبًا إفصاح أولي أُعيد عرضه لاحقًا.',
  'crosscheck.causeHint.scope_attributable': 'ليس خطأ: الإعلان يذكر حصة مساهمي الشركة الأم بينما بند القوائم هو الإجمالي شاملًا الحصص غير المسيطرة.',
  'crosscheck.causeHint.scope_restated': 'ليس خطأ: أُخذ الرقم من عمود المقارنة في إفصاح لاحق أعادت الشركة عرضه.',
  'crosscheck.causeHint.concept_mismatch': 'الصفّان غير قابلين للمقارنة؛ الربط بينهما خاطئ ويجب تضييقه.',
  'crosscheck.causeHint.unresolved': 'لم يستطع نمط الفرق ولا الحَكَم تحديد السبب.',
  'company.summary': 'النشاط',
  'company.summary.other': 'عرض الملخص باللغة {lang}',
  'company.ledger': 'المُستخرَج',
  // Labels beside a count of any size, so the bare plural -- the accusative
  // tamyiz («جدولاً») is only correct after 11-99.
  'company.ledger.facts': 'القيم',
  'company.ledger.tables': 'الجداول',
  'company.ledger.notes': 'الإيضاحات',
  'company.ledger.checks': 'الفحوص',
  'company.latestAuditor': 'آخر مراجع',
  'company.opinion': 'نوع الرأي',
  'company.signer': 'وقّعه',
  'company.licence': 'الترخيص',
  'company.firm': 'المكتب',
  'company.reportDate': 'تاريخ التقرير',
  'company.currency': 'العملة',
  'company.rounding': 'التقريب',
  'company.lastParsed': 'آخر تحليل',

  // --- financials -----------------------------------------------------
  'fin.statement': 'القائمة',
  'fin.period': 'الفترة',
  'fin.noConcepts':
    'تُطابَق الصفوف بمسمّياتها كما طُبعت، فليس في قاعدة البيانات ربطٌ بالمفاهيم؛ ولهذا لن تصطفّ معاً الصفوف التي تغيّرت صياغتها بين إفصاح وآخر.',
  'fin.noConceptsFix': 'شغّل `python -m parser.concepts --seed` لبناء هذا الربط.',
  'fin.source': 'المصدر',
  'fin.goToPage': 'صفحة {n}',

  // --- documents list -------------------------------------------------
  'docs.type': 'النوع',
  'docs.period': 'الفترة',
  'docs.periodEnd': 'نهاية الفترة',
  'docs.facts': 'القيم',
  'docs.year': 'السنة',
  'docs.lang': 'اللغة',
  'docs.pages': 'الصفحات',
  'docs.status': 'الحالة',
  'docs.passRate': 'النجاح',
  'docs.review': 'المفتوحة',
  'docs.xbrl': 'XBRL',

  // --- viewer ---------------------------------------------------------
  'viewer.page': 'صفحة {n}',
  'viewer.pageOf': 'صفحة {n} من {total}',
  'viewer.paneImage': 'الصفحة',
  'viewer.paneExtraction': 'المُستخرَج',
  'viewer.paneLabel': 'اللوحة المعروضة',
  'viewer.details': 'تفاصيل الإفصاح',
  'viewer.more': 'المزيد',
  'viewer.failingPages': '{n} صفحة فاشلة',
  'viewer.failingPages_one': 'صفحة واحدة فاشلة',
  'viewer.failingPages_two': 'صفحتان فاشلتان',
  'viewer.failingPages_few': '{n} صفحات فاشلة',
  'viewer.failingPages_many': '{n} صفحةً فاشلة',
  'viewer.goToPage': 'الانتقال إلى صفحة',
  'viewer.firstPage': 'الصفحة الأولى',
  'viewer.lastPage': 'الصفحة الأخيرة',
  'viewer.rail': 'الصفحات',
  'viewer.tier': 'المستوى',
  'viewer.fitWidth': 'ملء العرض',
  'viewer.fitPage': 'ملء الصفحة',
  'viewer.actualSize': 'الحجم الفعلي',
  'viewer.zoomIn': 'تكبير',
  'viewer.zoomOut': 'تصغير',
  'viewer.asSent': 'كما أُرسلت',
  'viewer.fullPage': 'الصفحة كاملة',
  'viewer.overlap': 'منطقة التداخل',
  'viewer.pdf': 'الملف الأصلي',
  'viewer.raw': 'الرد الخام',
  'viewer.digits': 'الأرقام',
  'viewer.digitsPrinted': 'كما طُبعت',
  'viewer.digitsWestern': 'بالأرقام اللاتينية',
  'viewer.shortcuts': 'الاختصارات',
  'viewer.tables': 'الجداول',
  'viewer.signatories': 'وقّعها',
  'viewer.signatoriesHint': 'الصفات كما طُبعت. أما التواقيع نفسها فبخط اليد، وتراها في صورة الصفحة المجاورة.',
  'viewer.source.scan': 'صورة ممسوحة',
  'viewer.source.text': 'طبقة نصية',
  'viewer.source.dpi': '{n} نقطة/بوصة',
  'viewer.source.chars': '{n} حرف',
  'viewer.source.noText': 'بلا طبقة نصية',
  'viewer.source.ink': 'تغطية الحبر {pct}',
  'viewer.source.rotated': 'مُدارة {n}°',
  'viewer.source.reversedDigits': '{n} مجموعة أرقام معكوسة',
  'viewer.goToNote': 'إيضاح {n}، صفحة {p}',
  'viewer.narratives': 'النص',
  'viewer.factsTyped': 'القيم المُصنَّفة',
  'viewer.factsHint': 'المحتوى نفسه الذي في الجدول أعلاه، خلية بخلية، مع إظهار كل تحويل',
  'viewer.checksFailed': 'فشل {n}',
  'viewer.checksAllPass': 'نجحت كلها ({n})',
  'viewer.noChecks': 'لا توجد فحوص لهذه الصفحة',
  'viewer.tableCount': '{n} جدول',
  'viewer.tableCount_one': 'جدول واحد',
  'viewer.tableCount_two': 'جدولان',
  'viewer.tableCount_few': '{n} جداول',
  'viewer.tableCount_many': '{n} جدولاً',
  'viewer.auditors': 'المراجع',
  'viewer.kam': 'أمور المراجعة الرئيسية',
  'viewer.nothingExtracted': 'لم يُستخرَج شيء من هذه الصفحة',
  'viewer.nothingExtractedWhy':
    'صُنِّفت الصفحة على أنها {pageType}، ولم تُنتج التشغيلة أي صفّ لها. وهذه نتيجةٌ بحدّ ذاتها، لا نقصٌ في هذه الشاشة.',
  'viewer.skippedPage': 'تم تجاوز هذه الصفحة عمداً',
  'viewer.skippedPageWhy': 'الصفحات المصنَّفة {pageType} لا تُرسَل إلى النموذج إطلاقاً.',
  'viewer.noImage': 'لا توجد صورة لهذه الصفحة',
  'viewer.imagePreparing': 'جارٍ تحضير الصورة…',
  'viewer.imageCommand': 'أنشئها بالأمر:',
  'viewer.nextFailure': 'الصفحة التالية بها فحص فاشل',
  'viewer.prevFailure': 'الصفحة السابقة بها فحص فاشل',
  'viewer.noFailures': 'لا توجد فحوص فاشلة في هذا المستند',
  'viewer.syncScroll': 'إعادة اللوحتين إلى الأعلى عند تغيير الصفحة',
  'viewer.repaired': 'أُصلح',
  'viewer.escalations': 'التصعيدات',
  'viewer.flags': 'العلامات',
  'viewer.illegible': 'مناطق غير مقروءة',

  // --- fact audit -----------------------------------------------------
  'fact.raw': 'كما طُبعت',
  'fact.western': 'بالأرقام اللاتينية',
  'fact.printed': 'كعدد',
  'fact.value': 'بعد تطبيق المُعامل',
  'fact.scale': 'المُعامل',
  'fact.label': 'المسمّى',
  'fact.column': 'العمود',
  'fact.period': 'الفترة',
  'fact.nil': 'معدومة',
  'fact.parens': 'بين قوسين',
  'fact.total': 'إجمالي',
  'fact.level': 'المستوى',
  'fact.note': 'إيضاح',
  'fact.concept': 'المفهوم',
  'fact.derivedWestern': 'التحويل اللاتيني أكمله خط المعالجة، لا النموذج',

  // --- quality --------------------------------------------------------
  'quality.title': 'الجودة',
  'quality.passRate': 'نسبة النجاح',
  'quality.passRateOf': '{pct} ({passed} من أصل {total})',
  'quality.errors': 'الأخطاء: {n}',
  'quality.warnings': 'التحذيرات: {n}',
  'quality.openReview': '{n} بند مراجعة مفتوح',
  'quality.openReview_one': 'بند مراجعة واحد مفتوح',
  'quality.openReview_two': 'بندا مراجعة مفتوحان',
  'quality.openReview_few': '{n} بنود مراجعة مفتوحة',
  'quality.openReview_many': '{n} بند مراجعة مفتوحاً',
  'quality.xbrl': 'التطابق مع XBRL',
  'quality.xbrlNone': 'لا يوجد ملف XBRL منشور يقابل هذا الإفصاح',
  'quality.expected': 'المتوقَّع',
  'quality.actual': 'الفعلي',
  'quality.delta': 'الفرق',
  'quality.detail': 'التفصيل',
  'quality.byCheck': 'حسب الفحص',
  'quality.dominant':
    '{pct} من بنود المراجعة المفتوحة يعود إلى فحص واحد، هو {check}. وهل ذلك خللٌ في الاستخراج أم تشدُّدٌ في الفحص؟ هذا بالضبط ما أُنشئ هذا التطبيق لتقرّره بنفسك.',
  'quality.severity.error': 'خطأ',
  'quality.severity.warn': 'تحذير',

  // --- empty states ---------------------------------------------------
  'empty.remuneration':
    'لا توجد صفوف مكافآت. مصدر هذه البيانات تقارير مجلس الإدارة، وليس في هذه المجموعة إلا تقرير واحد، وهو لم يُكمل خط المعالجة بعد.',
  'empty.governance':
    'لا توجد صفوف مجلس أو لجان. مصدر هذه البيانات تقارير مجلس الإدارة، وليس في هذه المجموعة إلا تقرير واحد، وهو لم يُكمل خط المعالجة بعد.',
  'empty.subsidiaries': 'لم تُستخرَج صفوف شركات تابعة من هذه الإفصاحات.',
  'empty.metrics': 'لم تُستخرَج مؤشرات تشغيلية أو مؤشرات استدامة من هذه الإفصاحات.',
  'empty.generic': 'لا يوجد شيء هنا',

  // --- about ----------------------------------------------------------
  'about.title': 'المجموعة',
  'about.whatIsParsed': 'ما تم تحليله',
  'about.whatIsNot': 'ما لم يتم تحليله',
  'about.complete': 'مكتملة',
  'about.completeWhy':
    'يُعدّ المستند مكتملاً حين يمرّ بكل المراحل — أي أن تكون حالته «مُتحقَّق منها» أو «تحتاج مراجعة». ولم يبلغ مستندٌ واحد حالة «مُتحقَّق منها»: فالمرحلة ١٢ تضع «تحتاج مراجعة» كلما أخفق أي فحص، وفي هذه المجموعة يُخفق شيء في كل إفصاح تقريباً. ولو اقتُصر على «مُتحقَّق منها» وحدها لظهر التطبيق فارغاً.',
  'about.incomplete': 'غير مكتملة بعد',
  'about.incompleteWhy': 'هذه مستبعدة من كل قائمة في هذا التطبيق.',
  'about.lastRun': 'آخر تشغيلة',
  'about.emptyTables': 'جداول بلا صفوف',
  'about.orphans': 'رموز مُحلَّلة ليست شركات مدرجة',
  'about.withFailures': 'مستندات بها فحص فاشل',
  'about.indexes': 'فهارس مسار الصفحة',
  'about.concepts': 'قاموس المفاهيم',

  // --- gaps -----------------------------------------------------------
  'gaps.title': 'الفجوات',
  'gaps.subtitle': 'ما ينقص من تحليل هذه الشركة، وما الذي يسدّه',
  'gaps.none': 'لا ينقص شيء',
  'gaps.noneWhy':
    'حُلِّل كل إفصاح منزَّل، وبلغ كل مستند حالةً نهائية، وقُرِئت كل صفحة مُخطَّطة.',
  'gaps.summary.complete': '{n} محلَّلة',
  'gaps.summary.missing': '{n} لم تُقرأ',
  'gaps.summary.stalled': '{n} متوقّفة',
  'gaps.summary.expected': 'من أصل {n} من الملفات المنزَّلة',
  'gaps.summary.review': '{items} من الفحوص المفتوحة على {pages} من الصفحات',
  'gaps.showing': 'يُعرض {n} من {total}',
  'gaps.showPages': 'اعرض الصفحات',
  'gaps.hidePages': 'إخفاء',
  'gaps.command': 'شغّله بنفسك',
  'gaps.notActionable': 'مرصود، ولا يُعالَج من هنا',
  'gaps.staleness': 'قُرِئ {when}',

  'gaps.hollowDot':
    'الخانة الفارغة في مصفوفة التغطية لا تُحتسب هنا إلا إذا كان الملف منزَّلاً فعلاً. أما ربعٌ لم تنشره السوق بعد فليس فجوة.',

  'gaps.notParsed': '{n} غير محلَّلة',
  'gaps.notParsedTitle': '{missing} مُنزَّلة ولم تُقرأ، و{stalled} توقّفت في منتصف الطريق. وهذا منفصل عن نسبة النجاح، فتلك تخصّ المستندات التي حُلِّلت فعلاً.',
  'gaps.kind.never_ingested': 'مُنزَّلة ولم تُقرأ',
  'gaps.kind.never_ingestedWhy':
    'نُزِّل الملف ولا صفَّ له في المُحلِّل. وهذه هي الفجوة التي لا تُظهرها مصفوفة التغطية.',
  'gaps.kind.stalled': 'توقّفت في منتصف الطريق',
  'gaps.kind.stalledWhy':
    'أُدخِلت لكنها لم تبلغ حالة نهائية. ويستأنف كل مستند من درجته الخاصة على السلّم.',
  'gaps.kind.page_never_extracted': 'صفحات لم تُرسَل',
  'gaps.kind.page_never_extractedWhy':
    'طلبت خطةُ العمل هذه الصفحات ولم يُخزَّن لها أي جواب.',
  'gaps.kind.extraction_failed': 'فشل الاستخراج',
  'gaps.kind.extraction_failedWhy':
    'أُرسِلت الصفحة ولم يتمكّن النموذج من قراءتها. أما الصفحة التي لا صفَّ لها إطلاقاً فهي واقعة أخرى: لم تُرسَل أصلاً.',
  'gaps.kind.failing_check': 'فحوص فاشلة',
  'gaps.kind.failing_checkWhy':
    'بنود مفتوحة في قائمة المراجعة. وهذا ما تتكوّن منه نسبة النجاح على البطاقة — لا نقصٌ في التغطية.',
  'gaps.kind.image_missing': 'صور صفحات ناقصة',
  'gaps.kind.image_missingWhy':
    'لا يوجد ملف على القرص بالمستوى الذي أسندته الخطة لهذه الصفحة.',
  'gaps.kind.concept_unmapped': 'قيم بلا مفهوم',
  'gaps.kind.concept_unmappedWhy':
    'مخزَّنة بمفهوم فارغ (NULL)، فلا يمكن مقارنتها بين الشركات. وربطها يستلزم إعادة مرحلة التوحيد.',
  'gaps.kind.no_ground_truth': 'لا شيء تُقارَن به',
  'gaps.kind.no_ground_truthWhy':
    'لا يوجد ملف XBRL منشور يقابل هذه الإفصاحات، فلم يُقارَن أي رقم فيها بأرقام السوق نفسها.',

  // --- the corpus gaps screen ------------------------------------------
  'corpus.gaps.title': 'الفجوات في المجموعة كلها',
  'corpus.gaps.subtitle': 'اختر الشركات التي تريد سدّ فجواتها. تُشغَّل واحدة تلو الأخرى في الخلفية.',
  'corpus.gaps.company': 'الشركة',
  'corpus.gaps.parsed': 'محلَّلة',
  'corpus.gaps.neverRead': 'لم تُقرأ',
  'corpus.gaps.stalled': 'متوقّفة',
  'corpus.gaps.incomplete': 'ناقصة',
  'corpus.gaps.review': 'فحوص مفتوحة',
  'corpus.gaps.planned': 'توكنات الخطة',
  'corpus.gaps.selectAll': 'الكل',
  'corpus.gaps.selectNone': 'لا شيء',
  'corpus.gaps.selectIncomplete': 'الناقصة فقط',
  'corpus.gaps.selected': '{n} محدَّدة',
  'corpus.gaps.nothing': 'لم تختر شيئاً',
  'corpus.gaps.runSelected': 'سدّ فجوات {n} من الشركات',
  'corpus.gaps.queueNote': 'شركة واحدة في كل مرة. يُسجَّل الإخفاق وتمضي البقية.',
  'corpus.gaps.overCap': 'تجاوز الحدّ المضبوط ({cap} توكن) — اختر عدداً أقل.',
  'corpus.gaps.overCapFull': 'يقدَّر هذا الاختيار بـ {n} توكن، وهو فوق الحدّ المضبوط ({cap}). اختر شركات أقل، أو ارفع EXPLORER_ACTIONS_MAX_TOKENS في ملف .env.',
  'corpus.gaps.capRoom': '{n} من {cap} توكن',
  'corpus.gaps.allDone': 'كل الشركات محلَّلة بالكامل.',
  'corpus.gaps.running': 'قيد التشغيل بالفعل',
  'batch.title': 'قبل تشغيل هذه الدفعة',
  'batch.companies': 'الشركات',
  'batch.plannedDocs': 'مستندات لها خطة',
  'batch.unplanned': 'لم تُفحَص بعد',
  'batch.unplannedNote': 'هذه بلا خطة عمل، فتكلفتها غير معروفة قبل تشغيل المرحلة ٢. وبمتوسط المجموعة قد تضيف نحو {n} توكن — وهو استقراء لا قياس.',
  'batch.skipped': 'تم تخطّي {n}: {why}',
  'batch.started': 'أُضيفت {n} شركة إلى قائمة الانتظار',

  // --- governance, remuneration, subsidiaries ---------------------------
  'gov.people': 'الأشخاص',
  'gov.memberships': 'المقاعد',
  'gov.flagged': '{n} صف بلا اسم مقروء',
  'gov.flagged_one': 'صفٌّ واحد بلا اسم مقروء',
  'gov.flagged_two': 'صفّان بلا اسم مقروء',
  'gov.flagged_few': '{n} صفوف بلا اسم مقروء',
  'gov.flagged_many': '{n} صفاً بلا اسم مقروء',
  'gov.flaggedWhy': 'تُرقّم صفحةُ السجل صفوفَها، فإذا قرأ النموذج الرقم الترتيبي على أنه الاسم، ظهر الصف هنا وكأنه شخص اسمه «٣». وقد بقيت هذه الصفوف ظاهرةً دون إخفاء، فهذا بالضبط نوع الخطأ الذي أُنشئ هذا التطبيق لكشفه.',
  'gov.body': 'الجهة',
  'gov.role': 'الدور',
  'gov.classification': 'التصنيف',
  'gov.years': 'السنوات',
  'gov.name': 'الاسم',
  'gov.unnamed': 'لم يُقرأ اسم',
  'gov.body.board': 'المجلس',
  'gov.body.other': 'أخرى',
  'rem.name': 'الاسم',
  'rem.total': 'المجموع',
  'rem.asPrinted': 'كما هي مطبوعة',
  'rem.year': 'السنة',
  'rem.allYears': 'كل السنوات',
  'rem.typedNote': 'المجموع هو القيمة التي اشتقّتها المرحلة ١٠. أما الخلايا تحته فهي ما طبعته الصفحة، تحت العنوان الذي استعملته.',
  'sub.name': 'الاسم',
  'sub.capital': 'رأس المال',
  'sub.ownership': 'نسبة الملكية',
  'sub.activity': 'النشاط الرئيسي',
  'sub.operation': 'تعمل في',
  'sub.incorporation': 'مُسجَّلة في',
  'empty.governanceNow': 'لا توجد بعد صفوف مجلس أو لجان لهذه الشركة. تأتي هذه البيانات من تقارير المجلس — سُدّ الفجوات لإضافتها.',
  'empty.remunerationNow': 'لا توجد بعد صفوف مكافآت لهذه الشركة. تأتي هذه البيانات من تقارير المجلس.',
  'empty.subsidiariesNow': 'لم تُستخرج بعد شركات تابعة لهذه الشركة.',

  // --- authentication ---------------------------------------------------
  'auth.signIn': 'تسجيل الدخول',
  'auth.signOut': 'تسجيل الخروج',
  'auth.username': 'اسم المستخدم',
  'auth.password': 'كلمة المرور',
  'auth.displayName': 'الاسم المعروض',
  'auth.displayNameHint': 'اختياري — كيف يظهر اسمك في التطبيق',
  'auth.submit': 'دخول',
  'auth.signingIn': 'جارٍ الدخول…',
  'auth.tagline': 'ما استُخرج من الإفصاحات، بجانب الورق الذي جاء منه',
  'auth.setupTitle': 'أنشئ أول حساب',
  'auth.setupWhy': 'لا توجد حسابات بعد. وأول حساب يكون مسؤولاً، ويُغلق هذا النموذج نهائياً بعد إنشائه.',
  'auth.setupSubmit': 'أنشئ حساب المسؤول',
  'auth.registerTitle': 'إنشاء حساب',
  'auth.registerSubmit': 'أنشئ الحساب',
  'auth.registerClosed': 'التسجيل مغلق. اطلب حساباً من المسؤول.',
  'auth.registerPending': 'أُنشئ حسابك وهو بانتظار موافقة المسؤول.',
  'auth.haveAccount': 'لديّ حساب',
  'auth.needAccount': 'إنشاء حساب',
  'auth.inviteCode': 'رمز الدعوة',
  'auth.changeTitle': 'اضبط كلمة مرور جديدة',
  'auth.changeWhy': 'كلمة مرورك ضبطها المسؤول. اختر كلمةً خاصة بك قبل المتابعة.',
  'auth.currentPassword': 'كلمة المرور الحالية',
  'auth.newPassword': 'كلمة المرور الجديدة',
  'auth.confirmPassword': 'تأكيد كلمة المرور',
  'auth.mismatch': 'الكلمتان غير متطابقتين',
  'auth.changeSubmit': 'غيّر كلمة المرور',
  'auth.changedSignIn': 'تغيّرت كلمة المرور. سجّل الدخول بالجديدة.',
  'auth.minLength': '{n} أحرف على الأقل',
  'auth.signedInAs': 'مسجَّل الدخول باسم {name}',
  'auth.account': 'الحساب',

  // --- the admin panel --------------------------------------------------
  'admin.title': 'المستخدمون',
  'admin.subtitle': 'من يستطيع الدخول، وما الذي يستطيع فعله.',
  'admin.newUser': 'أضف مستخدماً',
  'admin.username': 'اسم المستخدم',
  'admin.name': 'الاسم',
  'admin.role': 'الدور',
  'admin.role.admin': 'مسؤول',
  'admin.role.viewer': 'قارئ',
  'admin.roleWhy': 'المسؤول يرى الجودة والفجوات والتشغيلات، ويستطيع الإنفاق على الاستخراج. أما القارئ فيتصفّح المجموعة فقط.',
  'admin.status': 'الحالة',
  'admin.active': 'نشط',
  'admin.disabled': 'معطَّل',
  'admin.pending': 'بانتظار الموافقة',
  'admin.created': 'أُنشئ',
  'admin.lastLogin': 'آخر دخول',
  'admin.never': 'لم يحدث بعد',
  'admin.sessions': 'الجلسات',
  'admin.actions': 'إجراءات',
  'admin.enable': 'تفعيل',
  'admin.disable': 'تعطيل',
  'admin.approve': 'موافقة',
  'admin.resetPassword': 'إعادة ضبط كلمة المرور',
  'admin.signOutAll': 'إنهاء الجلسات',
  'admin.delete': 'حذف',
  'admin.deleteConfirm': 'حذف {name}؟ لا يمكن التراجع.',
  'admin.you': 'أنت',
  'admin.registration': 'التسجيل',
  'admin.registration.closed': 'مغلق — المسؤولون ينشئون الحسابات',
  'admin.registration.invite': 'بالدعوة فقط',
  'admin.registration.request': 'يقدّم أي شخص طلباً، والموافقة للمسؤول',
  'admin.invites': 'الدعوات',
  'admin.newInvite': 'أنشئ دعوة',
  'admin.inviteLink': 'رابط الدعوة',
  'admin.inviteExpires': 'تنتهي',
  'admin.inviteUsed': 'استُخدمت',
  'admin.revoke': 'إلغاء',
  'admin.noInvites': 'لا توجد دعوات',
  'admin.tempPassword': 'كلمة مرور مؤقتة',
  'admin.mustChange': 'اطلب تغيير كلمة المرور عند أول دخول',
  'admin.created1': 'أُنشئ الحساب. سلّمه كلمة المرور هذه بقناة أخرى — فلن تُعرض مرة ثانية.',

  // --- jobs -----------------------------------------------------------
  'job.title': 'التشغيلات',
  'job.running': 'قيد التشغيل',
  'job.queued': 'في الانتظار',
  'job.done': 'انتهت',
  'job.failed': 'أخفقت',
  'job.cancelled': 'أُلغيت',
  'job.interrupted': 'انقطعت',
  'job.interruptedWhy': 'توقّف الخادم أثناء تشغيل هذه المهمة، والاستئناف يبدأ من المرحلة نفسها.',
  'job.step': 'الخطوة {n} من {total}',
  'job.stepNothingToDo': 'لا شيء للتنفيذ',
  'job.cancel': 'إلغاء',
  'job.resume': 'استئناف',
  'job.showLog': 'السجل',
  'job.hideLog': 'إخفاء السجل',
  'job.logEmpty': 'لم تكتب المرحلة شيئاً بعد.',
  'job.none': 'لا توجد تشغيلات بعد',
  'job.earlier': 'تشغيلات سابقة',
  'job.elapsed': 'مضى {d}',
  'job.remaining': 'يتبقّى نحو {d}',
  'job.reconnecting': 'إعادة الاتصال…',
  'job.leaveSafe': 'يستمر التنفيذ وإن غادرت الصفحة.',
  'job.disabled': 'الإجراءات معطّلة',
  'job.disabledWhy':
    'هذه النسخة تقرأ المجموعة ولا تغيّرها. ومع ذلك تذكر كل فجوة أدناه الأمرَ الذي يسدّها. ولتفعيل الإجراءات اضبط EXPLORER_ACTIONS_ENABLED=true — واقرأ ملاحظة الأمان في الخطة أولاً.',

  'job.outcome': 'ما الذي تغيّر',
  'job.outcomeNone': 'لم يتغيّر شيء.',
  'job.outcomeChecks': 'ارتفعت الفحوص الناجحة بمقدار {n}',
  'job.outcomeChecksWorse': 'انخفضت الفحوص الناجحة بمقدار {n}',
  'job.outcomeReview': 'انخفضت البنود المفتوحة بمقدار {n}',
  'job.outcomeReviewWorse': 'ارتفعت البنود المفتوحة بمقدار {n}',
  'job.outcomeFacts': '{n} قيمة',
  'job.outcomeConcepts': 'رُبطت {n} قيمة بمفهوم',
  'job.outcomePages': 'قُرِئت {n} صفحة',

  // --- estimating before spending --------------------------------------
  'estimate.title': 'قبل التشغيل',
  'estimate.documents': 'المستندات',
  'estimate.pages': 'الصفحات',
  'estimate.tokens': 'تقدير توكنات الإدخال',
  'estimate.cost': 'التكلفة المقدَّرة',
  'estimate.unknown': 'غير معروف قبل الفحص',
  'estimate.noCost': 'لم يُضبط سعر',
  'estimate.free': 'بلا استدعاءات للنموذج',
  'estimate.steps': 'المراحل التي ستُشغَّل',
  'estimate.confirm': 'شغّل',
  'estimate.cancel': 'ليس الآن',
  'estimate.calibration': 'عند معايرته على {docs} من المستندات المكتملة، يأتي التقدير عند {factor}× من الفعلي — فاحسب نحو {budget}× من الرقم أعلاه.',
  'estimate.lowerBound': 'هذا حدٌّ أدنى',

  'action.run': 'شغّل',
  'action.reread': 'أعد القراءة',
  'action.fillAll': 'سُدّ كل الفجوات',
}
